/**
 * Beta feedback config — THE TEAM'S EDITING SURFACE (Epic 25, Story 25.1).
 *
 * This single file holds everything that drives the in-app feedback layer:
 *   - `cadence`  — WHEN prompts appear (story / character / pulse spacing).
 *   - `questions`— WHAT is asked, in Finnish (the parent-facing copy).
 *   - `severity` — WHICH answers raise a safety flag for the admin view.
 *
 * To change a question, an option, or how often a prompt fires, edit this file
 * and ship a frontend-only redeploy. No database, no admin UI (Epic 25 keeps
 * this deliberately simple for a temporary ~2-month beta tool).
 *
 * Authoring rules:
 *   - All `label` / `prompt` / `commentPlaceholder` text is **Finnish**
 *     (Epic 25 Decision 3 — i18n intentionally skipped). Use "satu", informal
 *     "sinä", standard written Finnish (coding-standards i18n rule).
 *   - Option `value`s are **stable English keys** — NOT user-facing. Changing a
 *     `value` changes the stored data + severity rules; change `label` freely.
 *   - The config is validated against `FeedbackConfigSchema` on import
 *     (`shared/src/feedback/index.ts`) — a malformed edit fails loud at build
 *     /startup rather than shipping silently.
 *
 * Seeded values come from the team proposal §3–§9 (cadence + questions) and the
 * locked decisions in `docs/stories/epic-25-beta-feedback-layer.md`.
 *
 * @see shared/src/types/feedback.ts (shape)
 * @see shared/src/feedback/index.ts (validated export)
 */

import type { FeedbackConfig } from '../types/feedback';

export const feedbackConfigData: FeedbackConfig = {
  version: 1,

  // When prompts fire. Tune freely during beta (proposal §3/§5/§7 seeds).
  cadence: {
    story: { firstAfter: 2, repeatEvery: 3, maxPerWeek: 1 },
    character: { lightAfter: 1, detailedAfter: 2 },
    pulse: { afterStory: 5, afterDays: 14, minStories: 2, minDaysBetweenPulses: 14 },
  },

  // Shared heading shown above every survey's questions.
  surveyTitle: 'Auta meitä parantamaan Sagamoonia vastaamalla kysymyksiin',
  // Neutral comment placeholder (Decision 4 — no "write as an adult" reminder).
  commentPlaceholder: 'Vapaa kommentti (vapaaehtoinen)',
  submitLabel: 'Lähetä',
  skipLabel: 'Ohita',
  // Hint shown under multi-select questions so multi vs. single is discoverable.
  multiSelectHint: 'Valitse yksi tai useampi',
  // Success toast after a submission.
  thanksMessage: 'Kiitos palautteesta!',

  questions: {
    // --- Story feedback (proposal §4) -------------------------------------
    story: {
      comment: true,
      questions: [
        {
          id: 'story_rating',
          prompt: 'Millainen tämä satu oli?',
          multi: false,
          options: [
            { value: 'excellent', label: 'Erinomainen' },
            { value: 'good', label: 'Hyvä' },
            { value: 'ok', label: 'Ihan ok' },
            { value: 'not_right', label: 'Ei aivan kohdallaan' },
            // `concerning` value preserved — drives the severity safety flag.
            { value: 'concerning', label: 'Huolestuttava tai sopimaton' },
          ],
          followUp: {
            id: 'story_issues',
            showIf: ['not_right', 'concerning'],
            prompt: 'Mikä tuntui väärältä tai huolestutti?',
            multi: true,
            options: [
              { value: 'too_long', label: 'Liian pitkä' },
              { value: 'too_short', label: 'Liian lyhyt' },
              // safety-tag values (too_scary / age_level_not_suitable / content_issue)
              // preserved — they drive the severity flag; only labels reworded.
              { value: 'too_scary', label: 'Liian pelottava tai jännittävä' },
              { value: 'did_not_match', label: 'Ei vastannut ideaamme' },
              { value: 'character_off', label: 'Hahmo ei tuntunut oikealta' },
              { value: 'language_awkward', label: 'Kieli tai tyyli tuntui kömpelöltä' },
              {
                value: 'age_level_not_suitable',
                label: 'Ikätaso ei tuntunut sopivalta',
              },
              { value: 'content_issue', label: 'Sisällössä oli jotain ongelmallista' },
              { value: 'other', label: 'Muu syy' },
            ],
          },
        },
      ],
    },

    // --- Character feedback, light (after 1st character) ------------------
    character_light: {
      comment: true,
      questions: [
        {
          id: 'character_impression',
          prompt: 'Millaiselta ensimmäinen satuhahmonne vaikuttaa?',
          multi: false,
          options: [
            { value: 'child_likes', label: 'Se näyttää ja vaikuttaa lapselle mieluisalta' },
            { value: 'especially_good', label: 'Siinä on jotain erityisen onnistunutta' },
            {
              value: 'good_minor_tweak',
              label: 'Se on ihan hyvä, mutta kaipaisi pientä muokkausta',
            },
            { value: 'not_expected', label: 'Se ei oikein vastannut odotuksiamme' },
            { value: 'cant_say_yet', label: 'En osaa sanoa' },
          ],
        },
      ],
    },

    // --- Character feedback, detailed (after 2nd character) ---------------
    character_detailed: {
      comment: true,
      questions: [
        {
          id: 'character_experience',
          prompt: 'Miltä satuhahmon tekeminen tuntui?',
          multi: true,
          options: [
            { value: 'easy', label: 'Sen tekeminen oli helppoa' },
            { value: 'fun_for_child', label: 'Sen tekeminen oli lapsesta hauskaa' },
            { value: 'child_involved', label: 'Lapsi pääsi hyvin mukaan suunnitteluun' },
            { value: 'clear_instructions', label: 'Ohjeet ja valinnat olivat selkeitä' },
            { value: 'wanted_more_control', label: 'Olisimme halunneet vaikuttaa siihen enemmän' },
            {
              value: 'something_confusing',
              label: 'Prosessissa oli jokin hankala tai epäselvä kohta',
            },
            { value: 'cant_say_yet', label: 'En osaa sanoa' },
          ],
        },
      ],
    },
  },

  // --- Beta pulse waves (Story 25.4; proposal §8 seeds wave 1) -----------
  // Ordered lifecycle sequence: each household walks the list at its own pace
  // (wave 1 gated by tenure via cadence.pulse; waves 2..N by minDaysBetweenPulses).
  // Append a new wave by adding an entry here — no other code change needed.
  // NB (Story 25.4): wave 2 reuses the option values `too_long`/`too_short`,
  // which also exist in the story follow-up with near-identical labels. The
  // admin label map is value-keyed (last-write-wins) — this collision is
  // knowingly accepted (words are synonymous); do NOT rename the values.
  pulseWaves: [
    // Wave 1 — Overall (unchanged from the original single pulse).
    {
      comment: true,
      questions: [
        {
          id: 'pulse_best',
          prompt: 'Mikä tuntuu parhaalta tähän mennessä?',
          multi: true,
          options: [
            { value: 'own_character', label: 'Lapsen oman hahmon käyttäminen' },
            { value: 'idea_together', label: 'Sadun idean keksiminen' },
            { value: 'reading_story', label: 'Valmiin sadun lukeminen' },
            { value: 'safe_tone', label: 'Sadun turvallinen ja lempeä sävy' },
            { value: 'create_with_child', label: 'Sadun luominen lapsen kanssa' },
            { value: 'other', label: 'Muu' },
            { value: 'not_sure', label: 'En osaa sanoa' },
          ],
        },
        {
          id: 'pulse_improve',
          prompt: 'Mihin toivot parannusta?',
          multi: true,
          options: [
            { value: 'creating_character', label: 'Hahmon luomiseen' },
            { value: 'giving_idea', label: 'Sadun ideoimiseen' },
            { value: 'final_story', label: 'Valmiiseen satuun' },
            { value: 'story_length', label: 'Sadun pituuteen' },
            { value: 'cover_image', label: 'Sadun kansikuvaan' },
            { value: 'clarity', label: 'Sagamoonin käytön selkeyteen' },
            { value: 'other', label: 'Muu' },
            { value: 'not_sure', label: 'En osaa sanoa' },
          ],
        },
        {
          id: 'pulse_fit',
          prompt: 'Mihin tilanteisiin Sagamoon sopii perheessäsi parhaiten?',
          multi: true,
          options: [
            { value: 'bedtime', label: 'Iltasatu' },
            { value: 'calm_moment', label: 'Rauhallinen yhteinen hetki' },
            { value: 'childs_ideas', label: 'Lapsen omien ideoiden toteuttaminen' },
            { value: 'special_occasion', label: 'Synttäri- tai juhlasatu' },
            { value: 'trips', label: 'Matka tai viikonloppu' },
            { value: 'invent_together', label: 'Yhteinen tekeminen' },
            { value: 'other', label: 'Muu' },
            { value: 'not_sure_yet', label: 'En osaa sanoa' },
          ],
        },
      ],
    },

    // Wave 2 — Satujen ja hahmojen laatu (after ~2 weeks more use).
    {
      comment: true,
      questions: [
        {
          id: 'pulse2_characters',
          prompt: 'Miltä lapsesi oma hahmo ja satujen kuvat ovat tuntuneet?',
          multi: false,
          options: [
            { value: 'loved', label: 'Lapsi rakastaa niitä' },
            { value: 'good', label: 'Hyviltä' },
            { value: 'mixed', label: 'Vaihtelevilta' },
            { value: 'disappointing', label: 'Eivät oikein onnistuneet' },
            { value: 'no_custom', label: 'Emme ole luoneet omaa hahmoa' },
          ],
        },
        {
          id: 'pulse2_story_quality',
          prompt: 'Entä satujen laatu ja pituus?',
          multi: false,
          options: [
            { value: 'just_right', label: 'Juuri sopivia' },
            { value: 'too_long', label: 'Liian pitkiä' },
            { value: 'too_short', label: 'Liian lyhyitä' },
            { value: 'quality_varies', label: 'Laatu vaihtelee' },
            { value: 'not_engaging', label: 'Eivät oikein jaksaneet kiinnostaa' },
          ],
        },
      ],
    },

    // Wave 3 — Sopivuus ja suosittelu (later in the beta).
    {
      comment: true,
      questions: [
        {
          id: 'pulse3_fit',
          prompt: 'Kuinka hyvin Sagamoon on sopinut perheesi arkeen näin pidempään käytettynä?',
          multi: false,
          options: [
            { value: 'very_well', label: 'Todella hyvin' },
            { value: 'quite_well', label: 'Melko hyvin' },
            { value: 'occasionally', label: 'Silloin tällöin' },
            { value: 'not_really', label: 'Ei oikein' },
          ],
        },
        {
          id: 'pulse3_recommend',
          prompt: 'Suosittelisitko Sagamoonia toiselle perheelle?',
          multi: false,
          options: [
            { value: 'definitely', label: 'Ehdottomasti' },
            { value: 'probably', label: 'Luultavasti' },
            { value: 'maybe', label: 'Ehkä' },
            { value: 'probably_not', label: 'En usko' },
          ],
        },
      ],
    },
  ],

  // Which answers raise a safety flag (Decision 6: derived, never trusted).
  // MVP: only story feedback can flag. Concerning rating OR any safety tag ⇒ high.
  severity: {
    story_quality: {
      high: {
        ratingQuestionId: 'story_rating',
        ratingValues: ['concerning'],
        tagQuestionId: 'story_issues',
        tagValues: ['content_issue', 'too_scary', 'age_level_not_suitable'],
      },
    },
  },
};
